// Isolated adapter + pure view-model for the Turf Talk Operator Console
// Overview. Consumes the shared admin contract GET /api/v1/admin/ops/overview
// when present; otherwise composes an HONEST overview from already-live admin
// sub-endpoints (readiness, telemetry metrics, AI config, provider health,
// scheduler). Every field the current data can't verify reads "unknown /
// not reported" — nothing is invented. No secrets are ever surfaced here.
import { readinessOverallOk } from "./adminMetrics";

export const CONSOLE_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "data", label: "Data & Games" },
  { key: "players", label: "Players" },
  { key: "ai", label: "AI & Rooms" },
  { key: "shows", label: "Predictions & Character Show" },
  { key: "users", label: "Users & Moderation" },
  { key: "commerce", label: "Commerce" },
  { key: "telemetry", label: "Telemetry & Audit" },
];

export function canViewConsole(user) { return !!(user && user.is_admin === true); }

export const UNKNOWN = "unknown / not reported";
export function orUnknown(v) { return v === undefined || v === null || v === "" ? UNKNOWN : v; }

// Recursively hide any secret-like keys so provider health / raw payloads can
// never leak a key, token, or credential into the console.
const SECRET_RE = /key|token|secret|password|authorization|bearer|credential|apikey|api_key/i;
export function sanitizeProviderHealth(raw) {
  const walk = (v) => {
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === "object") {
      const out = {};
      for (const [k, val] of Object.entries(v)) out[k] = SECRET_RE.test(k) ? "•••• (hidden)" : walk(val);
      return out;
    }
    return v;
  };
  return walk(raw || {});
}

const on = (v) => v === true;

export const NOT_REPORTED = "Not reported";
// A version cell that carries an honest reason when a value is genuinely absent.
export function ver(value, reason) { return { value: value || null, reason: value ? null : reason }; }
// The exact label the Overview prints for provider "Configured" — tri-state so
// a missing field reads "Not reported", never "No".
export function configuredLabel(c) { return c === true ? "Yes" : c === false ? "No" : NOT_REPORTED; }

// Normalize subsystem state strings into one honest vocabulary:
// active | disabled | degraded | unavailable | killed | paused.
export function mapSubsystemState(s) {
  const x = String(s == null ? "" : s).toLowerCase();
  if (["active", "operating", "ok", "enabled", "on", "running"].includes(x)) return "active";
  if (["killed", "kill"].includes(x)) return "killed";
  if (["paused", "pause"].includes(x)) return "paused";
  if (["disabled", "off"].includes(x)) return "disabled";
  if (["degraded", "attention", "warning", "warn"].includes(x)) return "degraded";
  if (["unavailable", "unknown", ""].includes(x)) return "unavailable";
  return x;
}

// Adapter over the DEPLOYED provider-health payload (the same shape Data &
// Games renders). Prefers canonical fields, then falls back to the current
// sports.* / sports_provider_serving / sports_provider_selected contract.
// Never coerces a missing `configured` to false — it stays null so the UI can
// truthfully say "Not reported".
export function providerHealthView(health, fallback = {}) {
  const h = health && typeof health === "object" ? health : {};
  const s = h.sports && typeof h.sports === "object" ? h.sports : {};

  const name = s.provider || h.sports_provider_serving || h.sports_provider_selected || h.sports_provider || fallback.name || null;
  const enabled = s.enabled === true || h.sports_enabled === true || fallback.enabled === true;
  const ok = s.ok === true || h.sports_ok === true || h.ok === true || fallback.ok === true;

  let configured = null;
  if (typeof s.configured === "boolean") configured = s.configured;
  else if (typeof h.sports_configured === "boolean") configured = h.sports_configured;
  else if (typeof h.configured === "boolean") configured = h.configured;
  else if (name && enabled) configured = true;
  else if (typeof fallback.configured === "boolean") configured = fallback.configured;

  const refreshing = s.refreshing === true || h.refreshing === true || (fallback.refreshing === true && s.refreshing !== false && h.refreshing !== false);
  const lastRefresh = s.last_refresh || s.refreshed_at || h.last_refresh || h.refreshed_at || fallback.lastRefresh || null;
  const lastSuccess = s.last_success || s.last_success_at || h.last_success || fallback.lastSuccess || null;
  const stale = s.stale === true || h.stale === true || fallback.stale === true;
  const offline = s.offline === true || s.online === false || h.offline === true || fallback.offline === true;
  const error = s.error || h.error || fallback.error || null;

  let state = "unknown";
  if (offline) state = "offline";
  else if (error) state = "error";
  else if (stale) state = "stale";
  else if (ok || enabled || refreshing) state = "ok";

  return { name: name || null, configured, enabled, ok, refreshing, lastRefresh, lastSuccess, stale, offline, error, state, balldontlie: /balldontlie/i.test(String(name || "")) };
}

// Player readiness view — verified counts/coverage per league where reported,
// else "unknown". ALWAYS states that complete NCAAF rosters are unsupported.
export function playerReadinessView(source = {}) {
  const p = source.players || source.player_readiness || {};
  const league = (obj) => obj ? {
    count: obj.count ?? obj.players ?? null,
    coverage: obj.coverage ?? obj.coverage_note ?? null,
    lastRefresh: obj.last_refresh ?? obj.refreshed_at ?? null,
    state: obj.state ?? (obj.stale ? "stale" : obj.supported === false ? "unavailable" : (obj.count != null ? "ok" : "unknown")),
  } : { count: null, coverage: null, lastRefresh: null, state: "unknown" };
  return {
    nfl: league(p.nfl || p.NFL),
    ncaa: league(p.ncaa || p.NCAA),
    // Hard truth — never overridable by upstream flags.
    ncaafRostersSupported: false,
    note: "Complete NCAAF team rosters are not supported upstream. Verified players and honest coverage only.",
  };
}

// Compose a typed overview from live sub-endpoint payloads. Pure/testable.
export function composeOverviewFromParts({ readiness, aiConfig, providers, scheduler, desktopVersion } = {}) {
  const r = readiness || {};
  const feed = r.current_season_feed || {};
  const pv = r.providers || {};
  const show = r.character_show || {};
  const cards = r.featured_prediction_cards || {};

  const healthKnown = !!readiness;
  const healthy = healthKnown && readinessOverallOk(readiness);

  // Prefer the deployed provider-health payload (Data & Games); fall back to
  // the readiness providers/feed summary. Missing `configured` stays null.
  const provider = providerHealthView(providers, {
    name: pv.sports_provider || null,
    enabled: pv.ok === true ? true : undefined,
    ok: pv.ok === true,
    configured: pv.ok === true ? true : undefined,
    refreshing: feed.ok === true && feed.live_serving !== false,
    lastRefresh: feed.refreshed_at || r.generated_at || null,
    stale: feed.stale === true,
  });

  const characterKill = on(scheduler?.kill_switch) || on(scheduler?.global_kill);
  const aiKill = on(aiConfig?.kill_switch);
  const audioKill = on(pv.audio_provider_kill);

  const subsystems = {
    roomAi: { label: "Room AI", state: aiConfig == null ? "unavailable" : (aiKill ? "killed" : (aiConfig.enabled ? "active" : "disabled")) },
    predictions: { label: "Predictions", state: readiness == null ? "unavailable" : (cards.ok ? "active" : "degraded") },
    characterShows: { label: "Character Shows", state: readiness == null ? "unavailable" : (characterKill ? "killed" : ((show.ok && Number(show.strategy_version) >= 4) ? "active" : "degraded")) },
    voices: { label: "Branded voices", state: readiness == null ? "unavailable" : (audioKill ? "paused" : (show.audio_state === "ready" ? "active" : "degraded")) },
  };

  const killSwitches = [
    { key: "character_show", label: "Character Show kill switch", active: characterKill },
    { key: "ai_engagement", label: "Room AI kill switch", active: aiKill },
    { key: "audio_provider", label: "Audio/voice kill switch", active: audioKill },
  ];

  const interventions = [];
  if (!healthKnown) interventions.push("Operations readiness could not be verified — check backend connectivity.");
  else if (!healthy) interventions.push("One or more readiness checks need attention (see Overview readiness).");
  if (provider.stale) interventions.push("Sports feed is marked stale — trigger a provider refresh.");
  if (provider.offline) interventions.push("Sports provider is offline — live data may be unavailable.");
  if (provider.name && provider.configured === false) interventions.push("Sports provider is reporting not configured.");
  if (audioKill) interventions.push("Audio/voice kill switch is ENGAGED — Character Show voices are paused.");
  if (subsystems.characterShows.state === "degraded") interventions.push("No validated v4+ Character Show is currently ready.");

  // Overall health is derived from the normalized statuses. It is never
  // "healthy" without evidence, and never "attention" merely because optional
  // metadata (e.g. a build id) is absent.
  const evidence = healthKnown || provider.name != null || aiConfig != null;
  const problems = provider.state === "offline" || provider.state === "error" || provider.state === "stale"
    || subsystems.predictions.state === "degraded" || subsystems.characterShows.state === "degraded"
    || subsystems.roomAi.state === "killed" || subsystems.characterShows.state === "killed" || subsystems.voices.state === "paused"
    || (healthKnown && !healthy);
  const healthState = !evidence ? "unknown" : (problems ? "attention" : "healthy");

  return {
    source: "composed",
    generatedAt: r.generated_at || null,
    health: { state: healthState, summary: healthState === "unknown" ? "Readiness not reported." : (healthState === "healthy" ? "All checks passing." : "Some checks need attention.") },
    versions: {
      backend: ver(r.backend_revision, "Backend did not report a revision"),
      desktop: ver(desktopVersion, "Set REACT_APP_BUILD to expose the desktop build id"),
      mobile: ver(null, "Mobile build is not exposed to the desktop client"),
    },
    provider,
    queues: Array.isArray(r.queues) ? r.queues : [],
    players: playerReadinessView(r),
    subsystems,
    killSwitches,
    interventions,
  };
}

// Coerce backend "not_reported"/"unknown" sentinels to null so the UI can show
// an honest "Not reported" with a reason rather than a misleading string.
function realOrNull(x) { return (x == null || x === "not_reported" || x === "unknown" || x === "") ? null : x; }

// Build the players view-model from the canonical overview `players` node.
function canonicalPlayersView(p = {}) {
  const verified = p.verified || {};
  const cov = p.coverage || {};
  const refresh = p.most_recent_refresh || null;
  return {
    nfl: { count: verified.NFL ?? verified.nfl ?? null, coverage: "Verified active-season players", lastRefresh: refresh, state: (verified.NFL ?? verified.nfl) != null ? "ok" : "unknown" },
    ncaa: { count: verified.NCAA ?? verified.ncaa ?? null, coverage: `Partial · ${cov.team_rosters_cached ?? 0} rosters cached (active/season/game stats only)`, lastRefresh: refresh, state: (verified.NCAA ?? verified.ncaa) != null ? "ok" : "unknown" },
    ncaafRostersSupported: false,
    note: p.ncaaf_roster_note || "Complete NCAAF team rosters are not supported upstream. Verified players and honest coverage only.",
  };
}

// Normalize a live /admin/ops/overview payload into the typed view-model. The
// CANONICAL deployed contract (core / surfaces / sports.health / ai / shows /
// players) is mapped first; a legacy/simple {provider,versions,subsystems}
// shape is still supported for backwards compatibility.
export function normalizeOverview(raw, { desktopVersion } = {}) {
  if (!raw || typeof raw !== "object") return null;

  const isCanonical = !!(raw.core || raw.surfaces || (raw.sports && raw.sports.health));
  if (isCanonical) return normalizeCanonicalOverview(raw, { desktopVersion });

  // ── legacy / simple shape ──────────────────────────────────────────────
  const v = raw.versions || {};
  const pr = raw.provider || raw.sports_provider || {};
  const subsRaw = raw.subsystems || {};
  const sub = (k, label) => {
    const s = subsRaw[k] || raw[k] || {};
    const st = s.state || (s.operating === true || s.active === true ? "active" : s.killed ? "killed" : s.paused ? "paused" : s.disabled ? "disabled" : "unavailable");
    return { label, state: mapSubsystemState(st) };
  };
  const kills = Array.isArray(raw.kill_switches) ? raw.kill_switches.map((k) => ({ key: k.key || k.name, label: k.label || k.name, active: k.active === true || k.on === true })) : [];
  const prObj = pr && typeof pr === "object" ? pr : {};
  const provider = providerHealthView(prObj.sports || prObj.sports_provider_serving ? prObj : (raw.sports_health || raw.provider_health || {}), {
    name: (typeof pr === "string" ? pr : (prObj.name || prObj.sports_provider)) || null,
    configured: typeof prObj.configured === "boolean" ? prObj.configured : undefined,
    enabled: prObj.enabled === true ? true : undefined,
    ok: prObj.ok === true,
    refreshing: prObj.refreshing === true,
    lastRefresh: prObj.last_refresh || prObj.refreshed_at || null,
    stale: prObj.stale === true,
    offline: prObj.offline === true,
  });

  return {
    source: "live",
    generatedAt: raw.generated_at || null,
    health: { state: raw.health?.state || (raw.healthy === true ? "healthy" : raw.healthy === false ? "attention" : "unknown"), summary: raw.health?.summary || raw.health_summary || "" },
    versions: {
      backend: ver(v.backend ?? raw.backend_revision, "Backend did not report a revision"),
      desktop: ver(v.desktop ?? desktopVersion, "Set REACT_APP_BUILD to expose the desktop build id"),
      mobile: ver(v.mobile, "Mobile build not reported by backend"),
    },
    provider,
    queues: Array.isArray(raw.queues) ? raw.queues : [],
    players: playerReadinessView(raw),
    subsystems: {
      roomAi: sub("room_ai", "Room AI"),
      predictions: sub("predictions", "Predictions"),
      characterShows: sub("character_shows", "Character Shows"),
      voices: sub("voices", "Branded voices"),
    },
    killSwitches: kills,
    interventions: Array.isArray(raw.interventions) ? raw.interventions : (Array.isArray(raw.needs_intervention) ? raw.needs_intervention : []),
    monitoring: normalizeMonitoring(raw),
  };
}


// ── Subsystem monitoring (overview.monitoring, schema_version:1) ──────────────
// Canonical subsystem names + honest display labels. Order preserved for render.
export const MONITORING_NAMES = [
  "backend", "database", "sports_provider", "data_freshness", "room_ai",
  "predictor", "character_show", "media_audio", "auth", "email", "telemetry_ingestion",
];
const MONITORING_LABELS = {
  backend: "Backend",
  database: "Database",
  sports_provider: "Sports provider",
  data_freshness: "Data freshness",
  room_ai: "Room AI",
  predictor: "Predictor",
  character_show: "Character Show",
  media_audio: "Media & audio",
  auth: "Auth",
  email: "Email delivery",
  telemetry_ingestion: "Telemetry ingestion",
};
export const MONITORING_STATES = ["green", "amber", "red", "inactive", "unverified"];
export function monitoringLabel(name) { return MONITORING_LABELS[name] || String(name || "").replace(/_/g, " "); }
// Any state outside the known vocabulary reads "unverified" (never green).
export function mapMonitoringState(s) {
  const x = String(s == null ? "" : s).toLowerCase().trim();
  return MONITORING_STATES.includes(x) ? x : "unverified";
}

// Redact any credential-looking substring from operator-facing reason/action
// copy (e.g. "token=abc123" → "token=•••• (hidden)"). Empty → null.
const CRED_VALUE_RE = /((?:api[_-]?key|apikey|key|token|secret|password|authorization|bearer|credential)s?\s*[:=]\s*)(\S+)/gi;
export function sanitizeMonitoringText(s) {
  if (s == null) return null;
  const str = String(s);
  if (!str.trim()) return null;
  return str.replace(CRED_VALUE_RE, (_m, p1) => `${p1}•••• (hidden)`);
}

// Normalize overview.monitoring. Absent on old backends → {present:false} so the
// UI shows "Not reported" rather than a fabricated green/healthy state.
export function normalizeMonitoring(rawOverview) {
  const m = rawOverview && typeof rawOverview === "object" ? rawOverview.monitoring : null;
  if (!m || typeof m !== "object") return { present: false };
  const subsRaw = m.subsystems && typeof m.subsystems === "object" ? m.subsystems : {};
  // Canonical order first, then any extra names the backend may add.
  const names = [
    ...MONITORING_NAMES.filter((n) => n in subsRaw),
    ...Object.keys(subsRaw).filter((n) => !MONITORING_NAMES.includes(n)),
  ];
  const subsystems = names.map((name) => {
    const s = subsRaw[name] || {};
    return {
      name,
      label: monitoringLabel(name),
      state: mapMonitoringState(s.state),
      affected: s.affected || name,
      lastSuccess: s.last_success || null,
      lastSync: s.last_sync || null,
      lastFailure: s.last_failure || null,
      failureReason: sanitizeMonitoringText(s.failure_reason),
      recommendedAction: sanitizeMonitoringText(s.recommended_action),
      observedAt: s.observed_at || null,
      note: sanitizeMonitoringText(s.note),
    };
  });
  const alerts = Array.isArray(m.alerts) ? m.alerts.map((a) => ({
    affected: a.affected || null,
    label: a.affected ? monitoringLabel(a.affected) : null,
    state: mapMonitoringState(a.state),
    recommendedAction: sanitizeMonitoringText(a.recommended_action),
  })) : [];
  const sr = m.summary && typeof m.summary === "object" ? m.summary : {};
  const summary = {
    overall: mapMonitoringState(sr.overall),
    counts: sr.counts && typeof sr.counts === "object" ? sr.counts : {},
    note: typeof sr.note === "string" && sr.note.trim() ? sr.note.trim() : null,
  };
  return { present: true, schemaVersion: m.schema_version ?? null, generatedAt: m.generated_at || null, subsystems, alerts, summary };
}

// Combine existing interventions with monitoring alerts, de-duplicating so the
// operator sees each actionable item once. Only red/amber monitoring alerts are
// surfaced as interventions (green/inactive/unverified are not actionable).
export function mergeInterventions(interventions = [], monitoring = null) {
  const out = [];
  const seen = new Set();
  const push = (text) => {
    const t = String(text == null ? "" : text).trim();
    if (!t) return;
    const k = t.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push(t);
  };
  for (const i of (Array.isArray(interventions) ? interventions : [])) push(i);
  if (monitoring && monitoring.present && Array.isArray(monitoring.alerts)) {
    for (const a of monitoring.alerts) {
      if (a.state !== "red" && a.state !== "amber") continue;
      const label = a.label || a.affected || "Subsystem";
      push(a.recommendedAction ? `${label}: ${a.recommendedAction}` : `${label} needs attention (${a.state}).`);
    }
  }
  return out;
}

// Map the sports refresh queue. Fresh active (queued/running) work is
// INFORMATIONAL only — never an intervention. A genuine problem is surfaced
// ONLY when the backend explicitly flags it via delayed_backlog_count /
// warning / reason / stuck / status:"delayed".
export function refreshQueueView(rq) {
  const q = rq && typeof rq === "object" ? rq : {};
  const by = q.by_status || {};
  const active = Number(q.active_processing_count ?? q.active_backlog ?? ((Number(by.queued) || 0) + (Number(by.running) || 0))) || 0;
  const delayedCount = Number(q.delayed_backlog_count) || 0;
  const stuckCount = Number(q.stuck_count) || 0;
  // A `reason`/`warning` STRING is descriptive metadata (e.g. "Normal
  // processing — no delayed backlog") and must NEVER by itself mark the queue
  // delayed. Only explicit signals do: delayed/stuck counts, warning===true,
  // stuck===true, or status:"delayed".
  const delayed = delayedCount > 0 || stuckCount > 0 || q.warning === true || q.stuck === true || q.status === "delayed";
  const reason = (typeof q.reason === "string" && q.reason.trim()) ? q.reason.trim() : ((typeof q.warning === "string" && q.warning.trim()) ? q.warning.trim() : null);
  return {
    active,
    delayed,
    delayedCount,
    stuckCount,
    reason,
    note: active > 0 ? `${active} refresh job${active === 1 ? "" : "s"} processing` : "No active refresh work",
  };
}

// Map the CANONICAL deployed /admin/ops/overview payload. Every status is
// derived from real fields; absent build metadata reads "Not reported" with a
// reason, never a misleading failure. `configured` is honored as reported.
export function normalizeCanonicalOverview(raw, { desktopVersion } = {}) {
  const core = raw.core || {};
  const surfaces = raw.surfaces || {};
  const sportsNode = raw.sports || {};
  const sh = sportsNode.health || {};
  const rq = sportsNode.refresh_queue || {};
  const ai = raw.ai || {};
  const shows = raw.shows || {};
  const buildReason = surfaces.note || "Build metadata is not_reported unless the app self-publishes it.";

  const stale = sh.stale_or_error === true;
  const lastError = sportsNode.last_error || sh.last_error || null;
  // Refresh queue: fresh active (queued/running) work is INFORMATIONAL only and
  // must never be framed as a backlog/intervention. A genuine problem is
  // surfaced ONLY when the backend explicitly flags it.
  const rqView = refreshQueueView(rq);
  // The paid BALLDONTLIE plan has NO enforced local daily cap. Only treat a cap
  // as real when the backend explicitly enforces it; otherwise request counts
  // are telemetry only and must never be framed as "exhausted".
  const quotaEnforced = sh.daily_quota_enforced === true && sh.daily_quota_cap != null;
  const quotaExhausted = quotaEnforced && sh.daily_requests_used != null && Number(sh.daily_requests_used) >= Number(sh.daily_quota_cap) && Number(sh.daily_quota_cap) > 0;
  const provider = {
    name: sh.provider || sportsNode.provider || (raw.recent && raw.recent.sports_provider) || null,
    configured: typeof sh.configured === "boolean" ? sh.configured : null,
    enabled: sh.configured === true,
    ok: sh.configured === true && !stale && !lastError,
    refreshing: rqView.active > 0,
    refreshActivity: rqView.active > 0 ? rqView.note : null,
    lastRefresh: sh.last_attempted_sync || sh.last_successful_sync || null,
    lastSuccess: sh.last_successful_sync || null,
    stale,
    offline: false,
    error: lastError,
    mode: realOrNull(sh.mode),
    quotaEnforced,
    quotaNote: quotaEnforced ? null : "No local daily cap — request count is telemetry only.",
    state: stale ? "stale" : (lastError ? "error" : (sh.configured ? "ok" : "unknown")),
    balldontlie: /balldontlie/i.test(String(sh.provider || "")),
  };

  const aiKnown = ai && Object.keys(ai).length > 0;
  const aiKill = ai.kill_switch === true;
  const voiceKill = shows.voice_kill_switch === true;
  const showsKnown = shows && Object.keys(shows).length > 0;
  const episodes = shows.episodes_by_status ? Object.values(shows.episodes_by_status).reduce((a, b) => a + (Number(b) || 0), 0) : 0;
  const audioRows = Number(shows.episode_audio_rows) || 0;

  const subsystems = {
    roomAi: { label: "Room AI", state: !aiKnown ? "unavailable" : (aiKill ? "killed" : (ai.enabled ? (ai.model_configured === false ? "degraded" : "active") : "disabled")) },
    predictions: { label: "Predictions", state: !showsKnown ? "unavailable" : (provider.configured ? "active" : "degraded") },
    characterShows: { label: "Character Shows", state: !showsKnown ? "unavailable" : (episodes > 0 || audioRows > 0 ? "active" : "degraded") },
    voices: { label: "Branded voices", state: !showsKnown ? "unavailable" : (voiceKill ? "paused" : (audioRows > 0 ? "active" : "degraded")) },
  };

  // Kill switches are labelled unambiguously as kill switches; they render as
  // "Not engaged" / "Engaged" (never a bare "off" that implies the subsystem
  // itself is disabled).
  const killSwitches = [
    { key: "ai_engagement", label: "Room AI kill switch", active: aiKill },
    { key: "audio_provider", label: "Audio/voice kill switch", active: voiceKill },
    { key: "character_show", label: "Character Show kill switch", active: raw.character_global_kill === true || (raw.recent && raw.recent.character_global_kill === true) || false },
  ];

  const interventions = [];
  if (stale) interventions.push("Sports feed is marked stale/error — trigger a provider refresh.");
  if (lastError) interventions.push(`Sports provider reported an error: ${lastError}`);
  if (aiKill) interventions.push("Room AI kill switch is ENGAGED — agents will not chime in.");
  if (voiceKill) interventions.push("Audio/voice kill switch is ENGAGED — Character Show voices are paused.");
  if (quotaExhausted) {
    interventions.push(`BALLDONTLIE daily quota is exhausted (${sh.daily_requests_used}/${sh.daily_quota_cap}) — sync may pause until reset.`);
  }
  // Only a backend-flagged delay/stuck/warning is an intervention — never a
  // nonzero active count on its own.
  if (rqView.delayed) {
    const cnt = rqView.delayedCount || rqView.stuckCount || 0;
    interventions.push(`Sports refresh queue is delayed${cnt ? ` (${cnt} job${cnt === 1 ? "" : "s"})` : ""}${rqView.reason ? ` — ${rqView.reason}` : " — investigate stuck jobs."}`);
  }

  const coreOk = core.backend === "ok" && core.database === "ok";
  const evidence = coreOk || provider.name != null || aiKnown;
  const problems = !coreOk || provider.stale || !!provider.error
    || Object.values(subsystems).some((s) => ["killed", "degraded", "paused"].includes(s.state))
    || aiKill || voiceKill || rqView.delayed;
  const healthState = !evidence ? "unknown" : (problems ? "attention" : "healthy");

  const desktopVal = realOrNull(surfaces.desktop_build) || desktopVersion || null;

  return {
    source: "live",
    generatedAt: raw.generated_at || null,
    health: {
      state: healthState,
      summary: healthState === "healthy"
        ? "Core services OK; sports provider live."
        : healthState === "attention"
          ? "One or more subsystems need attention."
          : "Insufficient evidence to confirm health.",
    },
    versions: {
      backend: ver(realOrNull(surfaces.backend_version) || realOrNull(core.backend_revision), "Backend did not report a version/revision"),
      desktop: ver(desktopVal, realOrNull(surfaces.desktop_build) ? null : `${buildReason} (set REACT_APP_BUILD to expose the desktop build)`),
      mobile: ver(realOrNull(surfaces.mobile_build), buildReason),
    },
    provider,
    queues: [],
    refreshQueue: rqView,
    players: canonicalPlayersView(raw.players),
    subsystems,
    killSwitches,
    interventions,
    monitoring: normalizeMonitoring(raw),
  };
}
